import { useCallback, useEffect, useId, useRef, useState } from 'react';
import type { MouseEvent } from 'react';
import { okToLeave } from '../utils/okToLeave';
import { useBackToClose } from './useBackToClose';

/**
 * Turn a pop-up panel into a real dialog: screen readers announce it, Escape closes it, and
 * focus goes back to the button that opened it.
 *
 *   const dialog = useDialog(onClose);
 *   <div {...dialog.props} aria-labelledby={dialog.titleId}>
 *     <h2 id={dialog.titleId}>Add cost</h2>
 *
 * Spread `props` on the panel itself (the white box), not on the dark backdrop.
 *
 * - **Never lose work:** an Escape typed inside a text box only leaves the box. A second Escape
 *   closes. One stray key can't throw away a half-typed note.
 * - **Phones:** on open, focus lands on the panel itself, never in a text box, so no keyboard
 *   pops up. A field with `autoFocus` keeps its focus.
 * - **Stacked pop-ups:** Escape closes the top one only.
 * - An Escape already handled inside (a mention list, a dropdown) is left alone: those call
 *   `preventDefault()`.
 *
 * For pop-ups that stay mounted while hidden, pass `{ isOpen }`.
 *
 * **Unsaved typing:** pass `{ dirty }` (from `useDirty`). Then the backdrop, the X, Cancel, the
 * closing Escape, and the phone's back gesture all ask "Leave without saving your changes?"
 * first. Wire them with `backdropProps` and `requestClose`. A successful save should call the
 * raw close, so it never asks. When `dirty` is false nothing changes.
 */

interface Entry { id: symbol; el: () => HTMLElement | null }

/** Open dialogs, in the order they opened. */
const openDialogs: Entry[] = [];

/** The dialog Escape belongs to: the newest one that has no open dialog inside it. */
function topDialog(): Entry | undefined {
  const els = openDialogs.map(d => d.el());
  for (let i = openDialogs.length - 1; i >= 0; i--) {
    const el = els[i];
    const holdsAnother = !!el && els.some((other, j) => j !== i && !!other && el.contains(other));
    if (!holdsAnother) return openDialogs[i];
  }
  return undefined;
}

const NOT_TEXT = new Set(['checkbox', 'radio', 'button', 'submit', 'reset', 'file', 'range', 'color', 'image']);

function isTextEntry(node: EventTarget | null): node is HTMLElement {
  if (!(node instanceof HTMLElement)) return false;
  if (node.isContentEditable) return true;
  if (node instanceof HTMLTextAreaElement) return true;
  return node instanceof HTMLInputElement && !NOT_TEXT.has(node.type);
}

export interface DialogOptions {
  /** Default true. Pass false while a mounted pop-up is hidden. */
  isOpen?: boolean;
  /** True when the person has typed something that would be lost. Close paths then ask first. */
  dirty?: boolean;
}

export function useDialog<T extends HTMLElement = HTMLDivElement>(onClose: () => void, options: DialogOptions = {}) {
  const { isOpen = true, dirty = false } = options;
  const ref = useRef<T>(null);
  const titleId = useId();
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;

  /** Close, but ask first when there is unsaved typing. */
  const requestClose = useCallback(() => {
    if (okToLeave(dirtyRef.current)) onCloseRef.current();
  }, []);

  /** Spread on the dark backdrop: a tap on the backdrop itself (not the panel) asks, then closes. */
  const backdropProps = {
    onClick: (e: MouseEvent) => { if (e.target === e.currentTarget) requestClose(); },
  };

  // Back gesture: only while dirty, so a clean pop-up behaves exactly as before. A "Stay" answer
  // spends the history marker, so the generation bump below pushes a fresh one.
  const [backGen, setBackGen] = useState(0);
  useBackToClose(isOpen && dirty, () => {
    if (okToLeave(true)) onCloseRef.current();
    else setBackGen(g => g + 1);
  }, `dialog-${titleId}-${backGen}`);

  useEffect(() => {
    if (!isOpen) return;
    const entry: Entry = { id: Symbol('dialog'), el: () => ref.current };
    openDialogs.push(entry);

    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const panel = ref.current;
    if (panel && !panel.contains(document.activeElement)) panel.focus({ preventScroll: true });

    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      if (topDialog()?.id !== entry.id) return;
      e.preventDefault();
      if (isTextEntry(e.target)) {
        e.target.blur();
        ref.current?.focus({ preventScroll: true });
        return;
      }
      requestClose();
    };
    window.addEventListener('keydown', onKey);

    return () => {
      window.removeEventListener('keydown', onKey);
      const at = openDialogs.indexOf(entry);
      if (at !== -1) openDialogs.splice(at, 1);
      if (opener && opener !== document.body && opener.isConnected) opener.focus({ preventScroll: true });
    };
  }, [isOpen, requestClose]);

  return {
    titleId,
    requestClose,
    backdropProps,
    props: {
      ref,
      role: 'dialog' as const,
      'aria-modal': true as const,
      tabIndex: -1,
    },
  };
}
